<template>
  <div class="d-flex justify-center align-start time-entry">
    <div>
      <v-text-field
        :class="{ 'no-timezone': hideTimezone }"
        density="compact"
        v-bind="$props"
        class="flex-grow-1 text-center input mt-0"
        v-maska="inputMaskOptions"
        :data-testid="`${$attrs['test-id']}-input`"
        :placeholder="militaryTimeEnabled ? '13:00' : '12:00'"
        v-model="time"
        variant="outlined"
        hide-details="auto"
        :rules="timeRules"
        :validate-on="true ? 'blur' : undefined"
        @blur="formatTime"></v-text-field>
      <small class="text-right font-size-x-small" v-if="!hideTimezone">
        {{ timezone }}
      </small>
    </div>
    <div class="ml-2" v-if="!militaryTimeEnabled">
      <v-btn-toggle
        v-model="amPm"
        mandatory
        color="primary"
        class="align-end"
        divided
        density="comfortable"
        :disabled="disabled">
        <v-btn
          height="40"
          variant="outlined"
          value="am"
          :data-testid="`${$attrs['test-id']}-am-button`"
          :class="{ 'px-5': !compact, 'auto-width': compact }">
          AM
        </v-btn>
        <v-btn
          height="40"
          variant="outlined"
          value="pm"
          :data-testid="`${$attrs['test-id']}-pm-button`"
          :class="{ 'px-5': !compact, 'auto-width': compact }">
          PM
        </v-btn>
      </v-btn-toggle>
    </div>
  </div>
</template>

<script>
import { DateTime } from 'luxon';
import { vMaska } from 'maska/vue';

export default {
  directives: {
    maska: vMaska
  },
  props: {
    modelValue: {
      type: Object,
      required: false,
      default() {
        return {
          amPm: '',
          time: ''
        };
      }
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false
    },
    timezone: {
      type: String,
      required: true
    },
    militaryTimeEnabled: {
      type: Boolean,
      required: true
    },
    fontSizeLarge: {
      type: Boolean,
      default: true
    },
    inline: {
      type: Boolean,
      default: false
    },
    required: {
      type: Boolean,
      default: false
    },
    compact: {
      type: Boolean,
      default: false
    },
    hideTimezone: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  data() {
    return {
      time: '',
      amPm: ''
    };
  },
  computed: {
    inputMaskOptions() {
      return {
        mask: '##:##',
        eager: true,
        token: {
          '#': {
            pattern: /[0-9]/,
            transform: (char, i, value) => {
              if (this.militaryTimeEnabled) {
                if (i === 0 && char > 2) {
                  return '';
                }
                if (i === 1 && value[0] === '2' && char > 3) {
                  return '';
                }
              } else {
                if (i === 0 && char > 1) {
                  return '';
                }
                if (i === 1 && value[0] === '1' && char > 2) {
                  return '';
                }
              }
              if (i === 2 && char > 5) {
                return '';
              }
              return char;
            }
          }
        }
      };
    },
    timeRules() {
      if (!this.required && this.time.length === 0) {
        return [];
      } else {
        return this.militaryTimeEnabled ? this.$validator.rules.time24 : this.$validator.rules.time;
      }
    }
  },
  methods: {
    formatTime() {
      if (!this.time) {
        return;
      }
      let parts = this.time.split(':');
      let hour = parts[0];
      let minute = parts[1];

      let hourNum = parseInt(hour, 10);
      let minuteNum = minute !== undefined && minute !== '' ? parseInt(minute, 10) : 0;

      // Reinterpret "649" -> "64:9" as 6:49 (user typed without colon)
      const reinterpretedMinute = (hourNum % 10) * 10 + minuteNum;
      const reinterpretedHour = Math.floor(hourNum / 10);
      const canReinterpret12h =
        !this.militaryTimeEnabled &&
        hourNum >= 13 &&
        hourNum <= 99 &&
        reinterpretedMinute <= 59 &&
        reinterpretedHour >= 1 &&
        reinterpretedHour <= 12;
      const canReinterpret24h =
        this.militaryTimeEnabled &&
        hourNum >= 24 &&
        hourNum <= 99 &&
        reinterpretedMinute <= 59 &&
        reinterpretedHour >= 0 &&
        reinterpretedHour <= 23;
      if (canReinterpret12h || canReinterpret24h) {
        hourNum = reinterpretedHour;
        minuteNum = reinterpretedMinute;
        hour = String(hourNum);
        minute = String(minuteNum);
      }

      // In 12-hour mode, do not accept or convert hours > 12; let validation show an error
      if (!this.militaryTimeEnabled && (hourNum > 12 || hourNum < 1)) {
        return;
      }

      if (hourNum < 10 && hour.substring(0, 1) !== '0') {
        hour = pad(hour);
      }

      if (!minute) {
        minute = '00';
      } else if (minuteNum < 10 && minute.length < 2) {
        minute = pad(minute);
      }

      if (minuteNum > 59) {
        return;
      }

      this.time = `${hour.slice(-2)}:${minute.slice(-2)}`;

      function pad(str, direction = 'left', pad = '0') {
        return direction === 'left' ? pad + str : str + pad;
      }
    },
    updateVal() {
      if (this.time.length === 5 && this.militaryTimeEnabled) {
        this.amPm = DateTime.fromFormat(this.time, 'HH:mm').toFormat('a');
      }

      this.$emit('update:model-value', {
        amPm: this.amPm,
        time: this.time
      });
    },
    updateLocalTime() {
      if (this.modelValue.time.length === 5 && this.militaryTimeEnabled) {
        this.time = DateTime.fromFormat(
          `${this.modelValue.time} ${this.modelValue.amPm}`,
          'hh:mm a'
        ).toFormat('HH:mm');
        this.amPm = DateTime.fromFormat(this.time, 'HH:mm').toFormat('a');
      } else {
        this.time = this.modelValue.time;
        this.amPm = this.modelValue.amPm;
      }
    }
  },
  beforeMount() {
    this.updateLocalTime();
  },
  watch: {
    modelValue: {
      handler() {
        this.updateLocalTime();
      },
      deep: true
    },
    amPm() {
      this.updateVal();
    },
    time(newVal) {
      // Reinterpret "64:9" or "64:90" (user typed 649 or 6490) as 6:49, or clear truly invalid values
      const isComplete = newVal && (newVal.length === 5 || newVal.length === 4);
      if (isComplete) {
        const parts = newVal.split(':');
        const hour = parseInt(parts[0], 10);
        const minute = parts[1] !== undefined && parts[1] !== '' ? parseInt(parts[1], 10) : 0;
        const reinterpMin = (hour % 10) * 10 + minute;
        const reinterpHour = Math.floor(hour / 10);
        const canFix12h =
          !this.militaryTimeEnabled &&
          hour >= 13 &&
          hour <= 99 &&
          reinterpMin <= 59 &&
          reinterpHour >= 1 &&
          reinterpHour <= 12;
        const canFix24h =
          this.militaryTimeEnabled &&
          hour >= 24 &&
          hour <= 99 &&
          reinterpMin <= 59 &&
          reinterpHour >= 0 &&
          reinterpHour <= 23;
        if (canFix12h || canFix24h) {
          const h = String(reinterpHour).padStart(2, '0');
          const m = String(reinterpMin).padStart(2, '0');
          this.time = `${h}:${m}`;
          this.updateVal();
          return;
        }
        // Only clear invalid when we have a full 5-char time (avoid clearing "64:9" before we fix it)
        if (newVal.length === 5) {
          const invalid12h = !this.militaryTimeEnabled && (hour > 12 || hour < 1 || isNaN(hour));
          const invalid24h = this.militaryTimeEnabled && (hour > 23 || hour < 0 || isNaN(hour));
          const invalidMinute = isNaN(minute) || minute < 0 || minute > 59;
          if (invalid12h || invalid24h || invalidMinute) {
            this.time = '';
            return;
          }
        }
      }
      this.updateVal();
    }
  }
};
</script>

<style scoped lang="scss">
.no-timezone {
  width: 75px;
}
</style>
